import {
  AppBar,
  Box,
  CssBaseline,
  SwipeableDrawer,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuRounded from "@mui/icons-material/MenuRounded";
import SearchRounded from "@mui/icons-material/SearchRounded";
import { motion } from "framer-motion";
import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { COLORS } from "../assets/theme";
import CommandPalette from "../components/layout/sidebar/CommandPalette";
import Sidebar from "../components/layout/sidebar/Sidebar";
import SidebarRail from "../components/layout/sidebar/SidebarRail";
import { NAV, resetRow } from "../components/layout/sidebar/navTheme";
import { useNavModel } from "../components/layout/sidebar/navModel";
import { useSwipeOpen } from "../components/layout/sidebar/useSwipeOpen";
import UserInfo from "../components/userInfo";
import { routes } from "../routes";
import { getTitleFromSidebar } from "../utils/helpers";
import { MOD_K, hasModifier } from "../utils/platform";

const COLLAPSED_KEY = "nav:collapsed";

const Layout: React.FC = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();
  const { groups, shortcuts } = useNavModel();

  const [collapsed, setCollapsed] = React.useState<boolean>(
    () => localStorage.getItem(COLLAPSED_KEY) === "true",
  );
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [paletteOpen, setPaletteOpen] = React.useState(false);
  // A press landing just after a backdrop dismiss would otherwise re-open.
  const drawerDismissedAtRef = React.useRef(0);

  const closeDrawer = React.useCallback(() => {
    setDrawerOpen(false);
    drawerDismissedAtRef.current = Date.now();
  }, []);

  const openDrawer = React.useCallback(() => {
    if (Date.now() - drawerDismissedAtRef.current < 250) return;
    setDrawerOpen(true);
  }, []);

  const toggleDrawer = React.useCallback(() => {
    if (drawerOpen) closeDrawer();
    else openDrawer();
  }, [drawerOpen, closeDrawer, openDrawer]);

  useSwipeOpen(isSmallScreen && !drawerOpen, openDrawer);

  const title =
    getTitleFromSidebar(
      routes.flatMap((item) => item.subpages ?? []),
      location.pathname,
    ) || "Dashboard";

  const [titleContext, titleLeaf] = React.useMemo(() => {
    const parts = title.split("/").map((part) => part.trim());
    const leaf = parts.pop() ?? title;
    return [parts.join(" / "), leaf];
  }, [title]);

  React.useEffect(() => {
    document.title = titleLeaf;
  }, [titleLeaf]);

  React.useEffect(() => {
    localStorage.setItem(COLLAPSED_KEY, String(collapsed));
  }, [collapsed]);

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (hasModifier(event) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setPaletteOpen((prev) => !prev);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // Also covers browser back/forward.
  React.useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  if (localStorage.getItem("access_token") === null) {
    return <Navigate to="/login" />;
  }

  const sidebarWidth = collapsed ? NAV.widthRail : NAV.widthExpanded;

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        "@supports (height: 100dvh)": { height: "100dvh" },
        overflow: "hidden",
      }}
    >
      <CssBaseline />

      {!isSmallScreen && (
        <Box
          component={motion.div}
          animate={{ width: sidebarWidth }}
          initial={false}
          transition={NAV.springSoft}
          sx={{ flexShrink: 0, height: "100%", overflow: "hidden" }}
        >
          {collapsed ? (
            <SidebarRail
              groups={groups}
              activeLink={location.pathname}
              onExpand={() => setCollapsed(false)}
              onOpenPalette={() => setPaletteOpen(true)}
            />
          ) : (
            <Box sx={{ width: NAV.widthExpanded, height: "100%" }}>
              <Sidebar
                onCollapse={() => setCollapsed(true)}
                onOpenPalette={() => setPaletteOpen(true)}
              />
            </Box>
          )}
        </Box>
      )}

      {isSmallScreen && (
        <SwipeableDrawer
          anchor="left"
          open={drawerOpen}
          onOpen={() => setDrawerOpen(true)}
          onClose={closeDrawer}
          disableBackdropTransition
          // Swipe-to-close only. Edge-swipe-to-open collides with the system
          // back gesture on both iOS and modern Android, so it stays off
          // rather than following MUI's per-platform default.
          disableSwipeToOpen
          ModalProps={{ keepMounted: true }}
          // Above the AppBar (drawer+1), which would otherwise paint over the
          // drawer header and hide its close button.
          sx={{ zIndex: theme.zIndex.drawer + 2 }}
          PaperProps={{
            sx: {
              width: "min(84vw, 320px)",
              backgroundColor: NAV.surface,
              backgroundImage: "none",
              borderRight: `1px solid ${NAV.border}`,
            },
          }}
        >
          <Sidebar
            variant="mobile"
            onCollapse={closeDrawer}
            onNavigate={closeDrawer}
          />
        </SwipeableDrawer>
      )}

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            backgroundColor: COLORS.WhiteBackground,
            borderBottom: `1px solid ${COLORS.dividerColor}`,
            zIndex: theme.zIndex.drawer + 1,
          }}
        >
          <Toolbar
            sx={{
              minHeight: { xs: 56, sm: 64 },
              px: { xs: 1, sm: 2.5 },
              gap: 1,
            }}
          >
            {isSmallScreen && (
              <Box
                component={motion.button}
                type="button"
                whileTap={{ scale: 0.9 }}
                transition={NAV.spring}
                onClick={toggleDrawer}
                aria-label="Open navigation"
                aria-expanded={drawerOpen}
                sx={{
                  ...resetRow,
                  width: 44,
                  height: 44,
                  ml: -0.5,
                  borderRadius: "12px",
                  display: "grid",
                  placeItems: "center",
                  color: COLORS.typographyColor,
                  "&:active": { backgroundColor: "rgba(0,0,0,0.06)" },
                }}
              >
                <MenuRounded sx={{ fontSize: 26 }} />
              </Box>
            )}

            <Box sx={{ minWidth: 0, flex: 1 }}>
              {titleContext && (
                <Typography
                  noWrap
                  sx={{
                    fontSize: "0.6875rem",
                    fontWeight: 600,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: COLORS.placeHolderColor,
                    lineHeight: 1.2,
                  }}
                >
                  {titleContext}
                </Typography>
              )}
              <Typography
                noWrap
                sx={{
                  color: COLORS.typographyColor,
                  fontSize: { xs: "1.0625rem", sm: "1.1875rem" },
                  fontWeight: 600,
                  lineHeight: 1.25,
                }}
              >
                {titleLeaf}
              </Typography>
            </Box>

            {!isSmallScreen && (
              <Box
                component="button"
                type="button"
                onClick={() => setPaletteOpen(true)}
                aria-label="Search pages"
                sx={{
                  ...resetRow,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  height: 36,
                  px: 1.25,
                  mr: 1,
                  borderRadius: "10px",
                  color: COLORS.placeHolderColor,
                  border: `1px solid ${COLORS.dividerColor}`,
                  backgroundColor: COLORS.White,
                  transition: `border-color 160ms ${NAV.ease}`,
                  "@media (hover: hover)": {
                    "&:hover": {
                      borderColor: COLORS.GOLD,
                      color: COLORS.typographyColor,
                    },
                  },
                }}
              >
                <SearchRounded sx={{ fontSize: 18 }} />
                <Typography sx={{ fontSize: "0.8125rem", color: "inherit" }}>
                  Search
                </Typography>
                <Box
                  component="kbd"
                  sx={{
                    fontFamily: "inherit",
                    fontSize: "0.6875rem",
                    fontWeight: 600,
                    color: "inherit",
                    border: `1px solid ${COLORS.dividerColor}`,
                    borderRadius: "5px",
                    px: 0.5,
                  }}
                >
                  {MOD_K}
                </Box>
              </Box>
            )}

            <UserInfo />
          </Toolbar>
        </AppBar>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            minHeight: 0,
            backgroundColor: COLORS.WhiteBackground,
            width: "100%",
            overflowX: "hidden",
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            overscrollBehavior: "none",
            scrollBehavior: "smooth",
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-track": { background: "rgba(0,0,0,0.05)" },
            "&::-webkit-scrollbar-thumb": {
              background: "rgba(0,0,0,0.2)",
              borderRadius: "3px",
              "&:hover": { background: "rgba(0,0,0,0.3)" },
            },
          }}
        >
          <Outlet />
        </Box>
      </Box>

      <CommandPalette
        open={paletteOpen}
        groups={groups}
        shortcuts={shortcuts}
        onClose={() => setPaletteOpen(false)}
      />
    </Box>
  );
};

export default Layout;
